import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Button } from "@mui/material";
import styles from "./ArticlePreview.module.css";
import { AiOutlineDoubleRight } from "react-icons/ai";
import * as Sentry from "@sentry/react";

// Vite will include all help article modules at build time
const modules = import.meta.glob('../../../help-articles/**/*.jsx');

const ArticlePreview = ({ title, path, onReadMore }) => {
  const [previewText, setPreviewText] = useState("");

  useEffect(() => {
    const loadArticle = async () => {
      try {
        const filePath = `../../../help-articles/${path}.jsx`;
        const importFunc = modules[filePath];
        if (!importFunc) {
          throw new Error(`Module not found for path: ${filePath}`);
        }

        const module = await importFunc();
        const ContentComponent = module.default;

        const extractText = (component) => {
          if (typeof component === "function") {
            const element = component();
            if (element.props?.children) {
              return extractTextFromChildren(element.props.children);
            }
          }
          return "";
        };

        const extractTextFromChildren = (children) => {
          if (Array.isArray(children)) {
            return children.map(extractTextFromChildren).join(" ");
          } else if (typeof children === "string") {
            return children;
          } else if (children?.props?.children) {
            return extractTextFromChildren(children.props.children);
          }
          return "";
        };

        const textContent = extractText(ContentComponent);
        const preview = textContent.split(". ").slice(0, 2).join(". ") + ".";
        setPreviewText(preview);
      } catch (error) {
        Sentry.captureException(error);
        console.error("❌ Error loading article preview:", error);
        setPreviewText("Unable to load preview.");
      }
    };

    loadArticle();
  }, [path]);

  return (
    <Box className={styles.articlePreview}>
      <Typography variant="h2" className={styles.title}>{title}</Typography>
      <Typography className={styles.content}>
        {previewText}
      </Typography>
      <Button 
        onClick={() => onReadMore(path)}
        className={styles.readMoreButton}
        startIcon={<AiOutlineDoubleRight />}
      >
        Read More
      </Button>
    </Box>
  );
};

ArticlePreview.propTypes = {
  title: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired, // changed from link → path
  onReadMore: PropTypes.func.isRequired,
};

export default ArticlePreview;